// クライアントコンポーネントとしてマーク

import { Button } from '@/shared/ui/button';
import { DatePicker } from '@/shared/ui/date-picker';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { BuildingType, buildingTypeMap, ProjectInsert } from '@/features/projects/types';
import { projectRepository } from '@/features/projects/repository';
import { validateProjectCreate } from '@/shared/lib/validation/projectValidation';
import { errorHandler, ErrorLevel } from '@/shared/lib/error/errorHandler';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';

/**
 * 新規プロジェクトモーダルのプロパティ
 */
interface NewProjectModalProps {
    children: React.ReactNode;
    onProjectCreated?: () => void; // 親コンポーネントへの作成通知
}

/**
 * 新規プロジェクトフォームのデータ
 */
interface NewProjectFormData {
    name: string;
    address: string;
    building_type: BuildingType | null;
    start_date: Date | null;
    end_date: Date | null;
    notes: string;
}

/**
 * 新規プロジェクトモーダル
 * @param param0 新規プロジェクトモーダルのプロパティ
 * @returns 新規プロジェクトモーダル
 */
export const NewProjectModal = ({ children, onProjectCreated }: NewProjectModalProps) => {
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<NewProjectFormData>({
        name: '',
        address: '',
        building_type: null,
        start_date: null,
        end_date: null,
        notes: '',
    });

    // 現在のユーザー情報を取得
    const { user: currentUser } = useCurrentUser();

    /**
     * 入力変更ハンドラー
     * @param e イベント
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * 選択変更ハンドラー
     * @param value 選択値
     */
    const handleSelectChange = (value: BuildingType) => {
        setFormData((prev) => ({ ...prev, building_type: value }));
    };

    /**
     * 開始日変更ハンドラー
     * @param date 日付
     */
    const handleStartDateChange = (date: Date | null) => {
        setFormData((prev) => ({ ...prev, start_date: date }));
    };

    /**
     * 終了日変更ハンドラー
     * @param date 日付
     */
    const handleEndDateChange = (date: Date | null) => {
        setFormData((prev) => ({ ...prev, end_date: date }));
    };

    /**
     * フォームの検証
     * @returns 検証結果
     */
    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            toast.error('プロジェクト名を入力してください');
            return false;
        }
        if (!formData.address.trim()) {
            toast.error('住所を入力してください');
            return false;
        }
        if (!currentUser) {
            toast.error('ユーザー認証に問題があります。再ログインしてください。');
            return false;
        }
        return true;
    };

    /**
     * フォームの送信
     * @param e イベント
     */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            // プロジェクト作成データを準備（バックエンドのProjectCreateに合わせる）
            const projectData: Record<string, unknown> = {
                name: formData.name.trim(),
                address: formData.address.trim(),
                building_type: formData.building_type || null,
                notes: formData.notes.trim() || null
            };

            // 日付が設定されている場合のみ追加
            if (formData.start_date) {
                projectData.start_date = formData.start_date.toISOString().split('T')[0];
            }
            if (formData.end_date) {
                projectData.end_date = formData.end_date.toISOString().split('T')[0];
            }


            // バリデーション実行
            const validation = validateProjectCreate(projectData);
            if (!validation.success) {
                const errorMessage = validation.errors
                    ? Object.values(validation.errors).flat().join(', ')
                    : validation.error || 'バリデーションエラーが発生しました';
                throw new Error(errorMessage);
            }

            await projectRepository.createProject(projectData as unknown as ProjectInsert);

            // キャッシュを更新（プロジェクト関連のすべてのキャッシュ）
            await Promise.all([
                // プロジェクト一覧のキャッシュを更新
                mutate(
                    (key) => typeof key === 'string' && key.startsWith('/projects'),
                    undefined,
                    { revalidate: true }
                ),
                // 日次活動のキャッシュを更新
                mutate(
                    (key) => typeof key === 'string' && key.startsWith('/daily-activities'),
                    undefined,
                    { revalidate: true }
                )
            ]);

            toast.success('プロジェクトが作成されました');
            setOpen(false);
            setFormData({
                name: '',
                address: '',
                building_type: null,
                start_date: null,
                end_date: null,
                notes: '',
            });

            // 親コンポーネントに作成を通知
            if (onProjectCreated) {
                onProjectCreated();
            }

            // 画面をリロードして新しいプロジェクトを表示
            // router.refresh(); // Not available in React Router

        } catch (error: unknown) {
            // エラーハンドリング
            const appError = errorHandler.handleError(
                error,
                {
                    operation: 'createProject',
                    userId: currentUser?.id,
                    tenantId: currentUser?.tenant_id,
                    additionalData: { formData }
                },
                ErrorLevel.HIGH
            );

            console.error('プロジェクト作成エラー:', appError);
            toast.error(appError.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle>新規プロジェクト作成</DialogTitle>
                    <DialogDescription>
                        新しいプロジェクトの基本情報を入力してください。プロジェクト作成後、工区や詳細情報を追加できます。
                        {currentUser && <span className="text-xs block mt-1">ユーザーID: {currentUser.id}</span>}
                        {currentUser && <span className="text-xs block mt-1">ロール: {currentUser.role}</span>}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                プロジェクト名 <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="col-span-3"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="address" className="text-right">
                                住所 <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                className="col-span-3"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="building_type" className="text-right">
                                建築種別
                            </Label>
                            <div className="col-span-3">
                                <Select
                                    value={formData.building_type || ''}
                                    onValueChange={(value) => handleSelectChange(value as BuildingType)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="建築種別を選択" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(buildingTypeMap).map(([value, label]) => (
                                            <SelectItem key={value} value={value}>
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="start_date" className="text-right">
                                開始日
                            </Label>
                            <div className="col-span-3">
                                <DatePicker
                                    selected={formData.start_date}
                                    onSelect={handleStartDateChange}
                                    placeholderText="開始日を選択"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="end_date" className="text-right">
                                終了予定日
                            </Label>
                            <div className="col-span-3">
                                <DatePicker
                                    selected={formData.end_date}
                                    onSelect={handleEndDateChange}
                                    placeholderText="終了予定日を選択"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-4 items-start gap-4">
                            <Label htmlFor="notes" className="text-right pt-2">
                                特記事項
                            </Label>
                            <Textarea
                                id="notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                className="col-span-3"
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            キャンセル
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? '作成中...' : 'プロジェクト作成'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}; 